import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Avatar } from "@/components/PersonCard";
import { years } from "@/lib/tree";
import { toast } from "sonner";
import { ClaimCard } from "@/components/ClaimCard";
import { DeleteRequestCard } from "@/components/DeleteRequestCard";
import { OrderRequestCard } from "@/components/OrderRequestCard";
import { NetWorthRequestCard } from "@/components/NetWorthRequestCard";
import { t } from "@/lib/i18n";

const relText = { child: "child of", parent: "parent of", spouse: "spouse of", elder_sibling: "elder sibling of", younger_sibling: "younger sibling of" };

const PendingCard = ({ p, user, onAction }) => {
  const act = async (kind) => {
    try {
      const { data } = await api.post(`/persons/${p.person_id}/${kind}`);
      toast.success(kind === "reject" ? "Entry rejected" : data.status === "approved" ? `${p.name} is now on the tree` : "Approval recorded");
      onAction();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const canApprove = user.can_vote && !p.i_approved && (user.is_admin || !p.mine);
  return (
    <div className="rounded-3xl border border-[#E5DEC9] bg-white p-6 flex flex-col md:flex-row gap-5 md:items-center" data-testid={`pending-card-${p.person_id}`}>
      <Avatar p={p} size="w-14 h-14 text-lg shrink-0" />
      <div className="flex-1 min-w-0 space-y-1">
        <p className="font-serif text-xl font-semibold">{p.name} <span className="font-mono text-xs text-[#7E8E85] font-normal">{years(p)}</span></p>
        <p className="text-sm text-[#52635B]">{relText[p.relation] ? <>{relText[p.relation]} <b>{p.relative_name || "unknown"}</b></> : "First root of the tree"}</p>
        <p className="text-xs text-[#7E8E85]">{p.is_self ? "Joined themselves" : `Added by ${p.submitted_by}`}</p>
        {p.bio && <p className="text-sm text-[#52635B] pt-1 line-clamp-2">{p.bio}</p>}
      </div>
      <div className="md:w-56 space-y-2">
        <p className="text-sm font-medium" data-testid={`pending-count-${p.person_id}`}>{p.approvals}/{p.needed} approvals</p>
        <Progress value={(p.approvals / p.needed) * 100} className="h-2" />
        <div className="flex gap-2 pt-1">
          {canApprove && <Button size="sm" onClick={() => act("approve")} className="rounded-full bg-[#1E3A2B] hover:bg-[#162C20]" data-testid={`approve-btn-${p.person_id}`}><Check className="w-4 h-4 mr-1" />{user.is_admin ? "Approve now" : "Approve"}</Button>}
          {p.i_approved && !user.is_admin && <span className="text-xs text-[#2E7D32] self-center">You approved</span>}
          {user.is_admin && <Button size="sm" variant="outline" onClick={() => act("reject")} className="rounded-full text-[#C62828]" data-testid={`reject-btn-${p.person_id}`}><X className="w-4 h-4 mr-1" />Reject</Button>}
        </div>
      </div>
    </div>
  );
};

export default function Approvals() {
  const { user, loading } = useAuth();
  const [items, setItems] = useState(null);
  const [claims, setClaims] = useState([]);
  const [dels, setDels] = useState([]);
  const [orders, setOrders] = useState([]);
  const [nws, setNws] = useState([]);
  const load = useCallback(() => {
    api.get("/pending").then(({ data }) => setItems(data)).catch(() => setItems([]));
    api.get("/claims").then(({ data }) => setClaims(data)).catch(() => setClaims([]));
    api.get("/delete-requests").then(({ data }) => setDels(data)).catch(() => setDels([]));
    api.get("/order-requests").then(({ data }) => setOrders(data)).catch(() => setOrders([]));
    api.get("/networth-requests").then(({ data }) => setNws(data)).catch(() => setNws([]));
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);
  if (loading) return null;
  if (!user) return <Navigate to="/join" replace />;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-14 space-y-8">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-3">{t("Family verification")}</p>
        <h1 className="font-serif text-4xl font-bold tracking-tight">{t("Pending approvals")}</h1>
        <p className="mt-3 text-[#52635B] max-w-2xl">Each new entry needs approval from 3 family members already on the tree. Removing a wrong entry or changing the elder → younger order needs 5 family approvals.
          {!user.can_vote && " You can approve others once you yourself are approved."}</p>
      </div>
      {nws.map((n) => <NetWorthRequestCard key={n.request_id} n={n} user={user} onAction={load} />)}
      {orders.map((o) => <OrderRequestCard key={o.request_id} o={o} user={user} onAction={load} />)}
      {dels.map((d) => <DeleteRequestCard key={d.request_id} d={d} user={user} onAction={load} />)}
      {claims.map((c) => <ClaimCard key={c.claim_id} c={c} user={user} onAction={load} />)}
      {items === null ? <div className="h-40 rounded-3xl bg-[#F4EDE2] animate-pulse" /> : items.length === 0 && claims.length === 0 && dels.length === 0 && orders.length === 0 && nws.length === 0 ? (
        <div className="rounded-3xl border border-dashed border-[#CDBF9F] p-14 text-center text-[#7E8E85]" data-testid="approvals-empty">{t("Nothing waiting for approval.")}</div>
      ) : items.map((p) => <PendingCard key={p.person_id} p={p} user={user} onAction={load} />)}
    </div>
  );
}
